import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import Categories from '../Categories/Categories';
import Warning from '../Warning/Warning';
import { useTheme } from '../../../theme';
import { ALL_DISHES_CATEGORY_ID, SELECTED_DISHES_CATEGORY_ID } from '../../constants';
import { getCategories, removeDuplicatesByName } from '../../utils';
import { Meal, MealViewMode } from '../../../Meal/types';
import { SelectedMeal } from '../../../MealPage/types';
import { useConfig } from '../../../context';
import { WxEvents } from '../../../DataLayerAnalytics/types';
import WebskyAnalyticsApollo from '../../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { isDemo } from '../../../utils';
import { useTranslation } from 'react-i18next';
import InfoMessages from '../../../InfoMessages';
import { InfoMessagePositionsEnum } from '@websky/graphql';
import { useRenderers } from '../../../renderProps';

interface Props {
	mealRenderer: (meal: Meal, segmentId: string, isSingleMeal?: boolean, mode?: MealViewMode) => React.ReactNode;
	activeSegmentId: string;
	meals: Meal[];
	selectedMeals?: SelectedMeal[];
	singleMealVariant: 'fullWidth' | 'standard';
}

const ServiceWrapper: React.FC<Props> = ({
	activeSegmentId,
	meals,
	selectedMeals = [],
	mealRenderer,
	singleMealVariant = 'standard'
}) => {
	const { t } = useTranslation('MealService');
	const { ServiceWrapper: css } = useTheme('MealService');

	const {
		Engine: { mealWarning, mealDefaultFilterButtonPlacement }
	} = useConfig();

	const { MealServiceWarning: MealServiceWarningOverride = Warning } = useRenderers();

	const [category, setCategory] = useState(mealDefaultFilterButtonPlacement === 'end' ? 0 : ALL_DISHES_CATEGORY_ID);

	const categories = useMemo(() => getCategories(meals, activeSegmentId), [meals, activeSegmentId]);

	const mealsBySegment = useMemo(() => meals.filter(meal => meal.segmentsId.includes(activeSegmentId)), [
		meals,
		activeSegmentId
	]);

	const { selectedMealsBySegment, selectedMealsCount, hasSelectedMeals } = useMemo(() => {
		const selectedMealsBySegment = selectedMeals.filter(meal => meal.segmentId === activeSegmentId);

		const selectedMealsCount = selectedMealsBySegment.reduce(
			(total, meal) => total + (meal.count - meal.confirmedCount),
			0
		);

		return {
			selectedMealsBySegment,
			selectedMealsCount,
			hasSelectedMeals: !!selectedMealsBySegment.length
		};
	}, [selectedMeals, activeSegmentId]);

	const mealIsSelected = (meal: Meal) => {
		return selectedMealsBySegment.find(selectedMeal => meal.id === selectedMeal.mealId);
	};

	const mealIsConfirmed = (meal: Meal) => {
		return selectedMealsBySegment.find(
			selectedMeal => meal.id === selectedMeal.mealId && selectedMeal.confirmedCount
		);
	};

	const filteredMeals: Meal[] = useMemo(() => {
		const filtered = mealsBySegment.filter(meal => {
			return (
				category === ALL_DISHES_CATEGORY_ID ||
				meal.category === categories[category] ||
				(hasSelectedMeals && category === SELECTED_DISHES_CATEGORY_ID && mealIsSelected(meal))
			);
		});

		return isDemo ? removeDuplicatesByName(filtered) : filtered;
	}, [mealsBySegment, category, hasSelectedMeals]);

	const confirmedMeals: Meal[] = useMemo(() => {
		const confirmedMeals = mealsBySegment.filter(mealIsConfirmed);

		return isDemo ? removeDuplicatesByName(confirmedMeals) : confirmedMeals;
	}, [mealsBySegment, category, selectedMeals]);

	useEffect(() => {
		// if user remove meal on selected dishes tab, return to first category
		if (category === SELECTED_DISHES_CATEGORY_ID && !selectedMealsCount) {
			setCategory(ALL_DISHES_CATEGORY_ID);
		}
	}, [selectedMeals, activeSegmentId, category]);

	useEffect(() => {
		WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.MealsList, { segmentId: activeSegmentId });
	}, [activeSegmentId]);

	return (
		<>
			<Categories
				categories={categories}
				onSelect={setCategory}
				selectedCategory={category}
				numOfSelectedMeals={selectedMealsCount}
				defaultGroupPlacement={mealDefaultFilterButtonPlacement}
			/>

			<InfoMessages
				position={InfoMessagePositionsEnum.meal}
				className={css.infoMessage}
				itemHtmlClassName={css.infoMessage__htmlMessage}
			/>

			{mealWarning && <MealServiceWarningOverride warning={mealWarning} />}

			<div
				className={cn(css.meals, {
					[css.meals_singleMeal]: filteredMeals.length === 1 && singleMealVariant === 'fullWidth'
				})}
			>
				{filteredMeals.map(meal =>
					mealRenderer(meal, activeSegmentId, filteredMeals.length === 1 && singleMealVariant === 'fullWidth')
				)}
			</div>
			{confirmedMeals?.length > 0 && (
				<>
					<div className={cn(css.meals_purchased)}>{t('Purchased')}</div>
					<div
						className={cn(css.meals, {
							[css.meals_singleMeal]: confirmedMeals.length === 1 && singleMealVariant === 'fullWidth'
						})}
					>
						{confirmedMeals.map(meal =>
							mealRenderer(
								meal,
								activeSegmentId,
								confirmedMeals.length === 1 && singleMealVariant === 'fullWidth',
								MealViewMode.confirmed
							)
						)}
					</div>
				</>
			)}
		</>
	);
};

export default ServiceWrapper;
